import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import { Badge, EmptyState, ErrorState, FilteredEmptyState, LoadingState } from '../components/States'
import { JsonDisclosure } from '../components/JsonDisclosure'
import type { AuditEntry } from '../lib/apiTypes'
import { formatCount, formatDateTimeWib } from '../lib/format'
import { useAuditLogs } from '../lib/hooks'

/**
 * Audit log: catatan setiap perubahan data oleh admin, dengan pelakunya, alamat
 * jaringan, dan request id untuk ditelusuri ke log server. Nilai secret tidak
 * pernah masuk ke daftar perubahan.
 */
export function AuditLogsPage() {
  const [params, setParams] = useSearchParams()
  const actor = params.get('actor') ?? ''
  const action = params.get('action') ?? ''
  const targetType = params.get('target_type') ?? ''
  const targetId = params.get('target_id') ?? ''
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''

  const [draft, setDraft] = useState({ actor, action, targetType, targetId, from, to })
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [extra, setExtra] = useState<AuditEntry[]>([])

  const query = useMemo(
    () => ({
      actor: actor || undefined,
      action: action || undefined,
      target_type: targetType || undefined,
      target_id: targetId || undefined,
      from: from || undefined,
      to: to || undefined,
      cursor,
    }),
    [actor, action, targetType, targetId, from, to, cursor],
  )

  const logs = useAuditLogs(query)
  const rows = cursor ? mergeRows(extra, logs.data?.data ?? []) : (logs.data?.data ?? [])
  const filtered = Boolean(actor || action || targetType || targetId || from || to)

  function apply(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const next = new URLSearchParams()
    if (draft.actor) next.set('actor', draft.actor)
    if (draft.action) next.set('action', draft.action)
    if (draft.targetType) next.set('target_type', draft.targetType)
    if (draft.targetId) next.set('target_id', draft.targetId)
    if (draft.from) next.set('from', draft.from)
    if (draft.to) next.set('to', draft.to)
    setParams(next, { replace: true })
    setCursor(undefined)
    setExtra([])
  }

  function clear() {
    setDraft({ actor: '', action: '', targetType: '', targetId: '', from: '', to: '' })
    setParams(new URLSearchParams(), { replace: true })
    setCursor(undefined)
    setExtra([])
  }

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Audit log</h1>
          <p className="muted">
            Setiap perubahan data tercatat dalam transaksi yang sama dengan perubahannya,
            jadi catatan ini tidak bisa ketinggalan. Perubahan hanya memuat field yang
            berubah, dan nilai rahasia tidak ikut dicatat.
          </p>
        </div>
      </div>

      <form className="card" onSubmit={apply}>
        <div className="form-grid">
          <div className="field">
            <label className="field__label" htmlFor="a-actor">
              Pelaku
            </label>
            <input
              id="a-actor"
              className="input"
              type="search"
              placeholder="email pengguna atau cli"
              value={draft.actor}
              onChange={(event) => setDraft({ ...draft, actor: event.target.value })}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="a-action">
              Aksi
            </label>
            <input
              id="a-action"
              className="input mono"
              type="search"
              placeholder="client.create"
              value={draft.action}
              onChange={(event) => setDraft({ ...draft, action: event.target.value })}
            />
            <span className="field__hint">
              Nama aksi persis, misalnya payment.resolve_review atau credential.update.
            </span>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="a-target-type">
              Jenis sasaran
            </label>
            <input
              id="a-target-type"
              className="input mono"
              type="search"
              placeholder="payment"
              value={draft.targetType}
              onChange={(event) => setDraft({ ...draft, targetType: event.target.value })}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="a-target-id">
              ID sasaran
            </label>
            <input
              id="a-target-id"
              className="input mono"
              type="search"
              value={draft.targetId}
              onChange={(event) => setDraft({ ...draft, targetId: event.target.value })}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="a-from">
              Dari tanggal
            </label>
            <input
              id="a-from"
              className="input"
              type="date"
              value={draft.from}
              onChange={(event) => setDraft({ ...draft, from: event.target.value })}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="a-to">
              Sampai tanggal
            </label>
            <input
              id="a-to"
              className="input"
              type="date"
              value={draft.to}
              onChange={(event) => setDraft({ ...draft, to: event.target.value })}
            />
          </div>
        </div>

        <div className="toolbar">
          <div className="toolbar__actions">
            <button type="submit" className="btn btn--primary">
              Terapkan
            </button>
            <button type="button" className="btn" onClick={clear} disabled={!filtered}>
              Bersihkan
            </button>
          </div>
        </div>
      </form>

      {logs.isPending ? (
        <LoadingState what="catatan audit" />
      ) : logs.isError ? (
        <ErrorState
          title="Gagal memuat audit log"
          error={logs.error}
          onRetry={() => void logs.refetch()}
          retrying={logs.isFetching}
        />
      ) : rows.length === 0 ? (
        filtered ? (
          <FilteredEmptyState what="catatan audit" onClear={clear} />
        ) : (
          <EmptyState
            title="Belum ada catatan audit"
            body="Catatan muncul setelah admin menjalankan aksi yang mengubah data, misalnya membuat client atau menyelesaikan tinjauan transaksi."
          />
        )
      ) : (
        <section aria-labelledby="audit-heading">
          <div className="page-head">
            <div className="page-head__text">
              <h2 id="audit-heading">{formatCount(rows.length)} catatan ditampilkan</h2>
              <p className="muted">Terbaru di atas.</p>
            </div>
          </div>
          <div className="rows">
            {rows.map((row) => (
              <article className="row-item" key={row.id}>
                <div className="row-item__main">
                  <span className="row-item__title mono">{row.action}</span>
                  <span className="row-item__meta">
                    {formatDateTimeWib(row.created_at)} oleh {row.actor || 'tanpa pelaku'}
                    {row.ip ? `, dari ${row.ip}` : ''}
                  </span>
                  <span className="row-item__meta">
                    Sasaran {row.target_type}
                    {row.target_id ? ` ${row.target_id}` : ''}
                    {row.request_id ? `, request id ${row.request_id}` : ''}
                  </span>
                  <JsonDisclosure summary="Buka perubahan" value={row.changes} />
                </div>
                <Badge tone="neutral">{row.target_type}</Badge>
              </article>
            ))}
          </div>
          <div className="state__actions">
            {logs.data?.next_cursor ? (
              <button
                type="button"
                className="btn"
                disabled={logs.isFetching}
                onClick={() => {
                  setExtra(rows)
                  setCursor(logs.data?.next_cursor)
                }}
              >
                {logs.isFetching ? 'Memuat...' : 'Muat berikutnya'}
              </button>
            ) : (
              <p className="muted">Semua catatan yang cocok sudah ditampilkan.</p>
            )}
          </div>
        </section>
      )}
    </>
  )
}

function mergeRows(previous: AuditEntry[], incoming: AuditEntry[]): AuditEntry[] {
  const seen = new Set(previous.map((row) => row.id))
  return [...previous, ...incoming.filter((row) => !seen.has(row.id))]
}
